import cx from "classnames";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { useTheme } from "context/ThemeContext/ThemeContext";

import { APP_ENTRY_ROUTE, NAV_LINKS } from "../landingContent";

const LINK_BASE = "rounded-full px-14 py-8 text-14 font-medium transition-colors";

/**
 * Sticky translucent header.
 *
 * The nav links and the logo lockup are inert while the site is gated — they
 * hold their place in the layout and reading order but navigate nowhere. The
 * theme control and the burger stay live: they are UI controls, not
 * destinations, and the burger is the only way to reach the menu on a phone.
 *
 * The theme control writes to the app's own ThemeContext rather than a
 * landing-only key, so the choice carries into the app when the visitor
 * launches it.
 */
export function LandingHeader() {
  const { theme, setThemeMode } = useTheme();
  const [menuOpen, setMenuOpen] = useState(false);
  const isDark = theme === "dark";

  /* The burger only exists below 940px; leaving the panel open past that
     breakpoint would strand it on screen. */
  useEffect(() => {
    if (!menuOpen) {
      return;
    }

    const mq = window.matchMedia("(min-width: 940px)");
    const close = () => mq.matches && setMenuOpen(false);

    close();
    mq.addEventListener("change", close);

    return () => mq.removeEventListener("change", close);
  }, [menuOpen]);

  return (
    <header
      className="sticky top-0 z-40 border-b"
      style={{
        background: "var(--nav-bg)",
        borderColor: "var(--line)",
        backdropFilter: "blur(14px)",
        WebkitBackdropFilter: "blur(14px)",
      }}
    >
      <div className="l-container l-header-row flex h-[68px] items-center gap-24">
        <div className="flex flex-none items-center gap-10">
          <span className="l-mark l-logo-mark h-34 w-34 text-15" style={{ letterSpacing: "-0.02em" }}>
            X
          </span>
          <span className="flex flex-col leading-1">
            <span
              className="l-logo-word text-20 font-extrabold"
              style={{ fontFamily: "var(--font-display)", letterSpacing: "-0.02em" }}
            >
              USDX
            </span>
            {/* Letters are distributed so the tracking matches the wordmark width. */}
            <span
              className="l-logo-sub mt-3 flex justify-between text-[7.5px] font-bold uppercase leading-1"
              style={{ color: "var(--brand400)" }}
            >
              <span>P</span>
              <span>E</span>
              <span>R</span>
              <span>P</span>
              <span>S</span>
            </span>
          </span>
        </div>

        <nav className="l-nav-desktop ml-8 items-center gap-4">
          {NAV_LINKS.map((item) => (
            <span
              key={item.href}
              aria-disabled="true"
              title="Coming soon"
              className={cx(LINK_BASE, "usdx-nav-link l-nav-disabled")}
            >
              {item.label}
            </span>
          ))}
        </nav>

        <div className="flex-1" />

        <button
          type="button"
          className="l-icon-btn"
          aria-label={isDark ? "Switch to light theme" : "Switch to dark theme"}
          onClick={() => setThemeMode(isDark ? "light" : "dark")}
        >
          <span className="text-15 leading-1">{isDark ? "☀" : "☾"}</span>
        </button>

        <Link
          to={APP_ENTRY_ROUTE}
          className="l-pill l-pill-primary l-header-cta h-38 flex-none gap-7 whitespace-nowrap px-20 text-14"
        >
          Launch App <span className="l-cta-chevron opacity-70">›</span>
        </Link>

        <button
          type="button"
          className="l-burger l-icon-btn !rounded-10"
          aria-label="Menu"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((open) => !open)}
          style={{ color: "var(--ink)" }}
        >
          ☰
        </button>
      </div>

      {menuOpen && (
        <div
          className="flex flex-col gap-2 border-t px-24 pb-18 pt-10"
          style={{ background: "var(--surface)", borderColor: "var(--line)" }}
        >
          {NAV_LINKS.map((item, index) => (
            <span
              key={item.href}
              aria-disabled="true"
              title="Coming soon"
              className="l-nav-disabled px-4 py-13 text-16 font-semibold"
              style={{
                borderBottom: index === NAV_LINKS.length - 1 ? undefined : "1px solid var(--line-soft)",
              }}
            >
              {item.label}
            </span>
          ))}
        </div>
      )}
    </header>
  );
}
